/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import ILegendLayer, { ILayerGroup } from '../../model/ILegendLayer';
import { Layer } from './Layer';
import Typography from '@mui/material/Typography';
import EyeVisible from '../../icons/EyeVisible.svg?react';
import EyeHidden from '../../icons/EyeHidden.svg?react';
import Down from '../../icons/Down.svg?react';
import Up from '../../icons/Up.svg?react';
import mikeSharedTheme, { mikePalette } from '../../styles/mikeSharedTheme';
import { useCallback, useMemo, useState } from 'react';
import MikeVisualizerLib from '../../MikeVisualizer/lib/MikeVisualizer';
import { IRenderElement } from '../../MikeVisualizer/lib/IMikeVisualizerModels';
import { useAppDispatch } from "../../store/store";
import { legendGroupHideAll, legendGroupShowAll } from "../../reducers/legend";
import { BASEMAP_ID } from '../../model/layerGroups';

const iconPrimaryStyle = (disabled = false) => {
  const color = disabled ? mikePalette.primary.light : mikePalette.primary.main
  return css`
    line {      
      stroke: ${color};
    }
    path {
      fill: ${color};
      stroke: ${color};
    }
  `
}

const TitleContainerStyle = (visible: boolean) => {
  const backgroundColor = visible ? mikePalette.mediumGrey?.main : mikePalette.mediumGrey?.light;  
  return css`
  display: flex;
  justify-content: space-between;
  align-items: center;  
  background-color: ${backgroundColor};
  border-top: 1px solid ${mikePalette.mediumGrey?.dark};
`;
}

const TitleAndButtonStyle = css`
  display: flex;
  align-items: center;
`;

export const TitleStyle = (visible: boolean) => {  
  const color = visible ? mikePalette.primary.main : mikePalette.primary.light;
  return css`
  padding-left: ${mikeSharedTheme.spacing(1)};
  padding-right: ${mikeSharedTheme.spacing(1)};
  &.MuiTypography-root {
    color: ${color};
  }  
`;
}

interface IProps {
  layerGroup: ILayerGroup; 
}

const colorStyle = (color: string) => {  
  return css`
    width: ${mikeSharedTheme.spacing(2)};
    height: ${mikeSharedTheme.spacing(2)};
    border-style: solid;
    border-color: ${color};  
  `;
}

export const LayerGroup = (props: IProps) => {
  const {layerGroup} = props;  
  const dispatch = useAppDispatch();
  const { getState } = MikeVisualizerLib;
  const { rendered2DElements } = getState();


  const loadedLayers = useMemo(() => {
    const layers = layerGroup.layer;  
    const two2DLayerIds = rendered2DElements.map((re: IRenderElement) => re.id)    
    const loadedLayers = layers.filter((l: ILegendLayer) => 
      two2DLayerIds.includes(l.id) || l.id === BASEMAP_ID)   
    return loadedLayers
  }, [layerGroup, rendered2DElements])


  const allLayersHidden = useMemo(() => {
    const hiddenLayers = loadedLayers.filter((l: ILegendLayer) => l.visible === false); 
    return hiddenLayers.length === loadedLayers.length
  }, [loadedLayers])
 
  const [layerGroupOpen, setLayerGroupOpen] = useState(true);

  const handleToggleVisibiltyAllLayers = useCallback(() => {
    const showAllLayers = allLayersHidden
    if (showAllLayers){
      dispatch(legendGroupShowAll(layerGroup))
    }
    else{
      dispatch(legendGroupHideAll(layerGroup))
    }
  }, [layerGroup, allLayersHidden, dispatch])
  
  return (     
     <div>
      <div css={TitleContainerStyle(!allLayersHidden)} key="layergroup">
        <div css={TitleAndButtonStyle}>
          {layerGroupOpen ?  <Up css={iconPrimaryStyle(allLayersHidden)}  onClick={() => setLayerGroupOpen(!layerGroupOpen)}/>: 
          <Down css={iconPrimaryStyle(allLayersHidden)} onClick={() => setLayerGroupOpen(!layerGroupOpen)} />}
          <Typography css= {TitleStyle(!allLayersHidden)}  variant="body2"><b>{layerGroup.title}</b></Typography>
        </div>
        {layerGroup.color ? <div css={colorStyle(layerGroup.color)}/> : null}        
        {allLayersHidden ? <EyeHidden  css={iconPrimaryStyle(true)} onClick={handleToggleVisibiltyAllLayers}/> : <EyeVisible onClick={handleToggleVisibiltyAllLayers}/>}
      </div>
      {layerGroupOpen && loadedLayers.map((l: ILegendLayer, index) => <Layer key={index} layer={l} layerGroup={layerGroup}/>)}
    </div>
  );
};

export default LayerGroup